import { ReactElement, useEffect } from 'react'
import { useQuery, NetworkStatus } from '@apollo/client'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { NavLink, useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import Heading from 'components/v2/Heading'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Content, ContentBody, ContentHeader } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { GET_INVENTORY_RULES } from 'features/InventoryRules/queries-legacy'
import { filterEmptyValue } from 'utils/object'
import { getQueries } from 'utils/route'
import { canRead, canWrite } from 'features/Users/utils'
import { InventoryRulesList } from './List'
import { InventoryRulesPagination } from './Pagination'
import { useLogin } from 'features/Login/hooks'

const LIMIT = 30

const messages = defineMessages({
  helmet: {
    id: 'inventoryRules.helmet',
    defaultMessage: 'Inventory Rules | Nexbus Admin'
  }
})

const InventoryRulesView = (): ReactElement => {
  let content
  const location = useLocation()
  const intl = useIntl()
  const login = useLogin()
  const { page = '1' } = getQueries(location.search)
  const currentPage = parseInt(page)

  const {
    loading,
    error,
    data = {},
    refetch,
    networkStatus
  } = useQuery(GET_INVENTORY_RULES, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: filterEmptyValue({
      limit: LIMIT,
      page: currentPage - 1
    })
  })

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
    refetch()
  }, [])

  if (!canRead(FEATURES.INVENTORY_RULE, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.INVENTORY_RULE} />
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="inventoryRules.error.description"
            defaultMessage="There was an error while loading your inventory rules"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button
            onClick={(): void => {
              refetch()
            }}
            kind="primary"
          >
            <FormattedMessage id="inventoryRules.error.action" defaultMessage="Reload your inventory rules" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.inventoryRules.items) && currentPage === 1) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="inventoryRules.empty.description"
            defaultMessage="You don&#39;t have any inventory rules yet"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button as={NavLink} to="/inventory-rules/new" kind="primary">
            <FormattedMessage id="inventoryRules.empty.action" defaultMessage="Add your first inventory rule" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.inventoryRules.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="inventoryRules.empty.filters.description"
            defaultMessage="There were no inventory rules found according to your filters"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button as={NavLink} to="/inventory-rules" kind="primary">
            <FormattedMessage id="inventoryRules.empty.filters.action" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <InventoryRulesList inventoryRules={data.inventoryRules.items} />

        <InventoryRulesPagination
          page={currentPage}
          limit={LIMIT}
          totalItems={data.inventoryRules.total}
          currentItemsCount={data.inventoryRules.items.length}
        />
      </>
    )
  }

  return (
    <Content hasMaxWidth={false}>
      <ContentHeader isResponsive className="">
        <Heading.H3>
          <FormattedMessage id="inventoryRules.header.label" defaultMessage="Inventory rules list" />
        </Heading.H3>
        {canWrite(FEATURES.INVENTORY_RULE, login.accessToken.policies) && (
          <Button as={NavLink} to="/inventory-rules/new" kind="primary">
            <FormattedMessage id="inventoryRules.header.new" defaultMessage="+ Add inventory rule" />
          </Button>
        )}
      </ContentHeader>
      <ContentBody>
        <section>{content}</section>
      </ContentBody>
    </Content>
  )
}

export default withLastUrl(InventoryRulesView)
